import type { RubricDimensionKey } from './rubricDimensions';

/**
 * What a low score on each dimension actually MEANS, and what to do about it.
 *
 * WHY THIS IS A LOOKUP AND NOT GENERATED TEXT
 *
 * The flags list used to say things like "a tier-1 dimension across 6 scored
 * calls — the rubric treats this as a move discovery cannot do without." That
 * restates the score's importance without saying anything about the calls. A
 * reader already knows a low number is bad; what they cannot get from the
 * number is WHICH BEHAVIOUR produced it.
 *
 * WHERE THE WORDS COME FROM. Every `problem` line below is a compression of
 * that dimension's own "1-2 =" column in
 * `services/utils/rubric/discovery_call_rubric.py` — the failure mode the
 * rubric itself describes, in Lushin's coaching language. Nothing here is
 * invented, and nothing here is a paraphrase of the score.
 *
 * ⚠️ THIS IS NOT PER-CALL EVIDENCE. The analyser writes a real
 * `what_was_missed` per category per session, but the rollup only aggregates
 * per-dimension SCORES — the narrative text is never rolled up, and the
 * super-admin read layer never scans tenant DBs live. So this says what a low
 * score on this dimension means in general, which is actionable, rather than
 * what happened on one specific call, which would need a rollup change.
 */

export interface DimensionGuidance {
  /** The behaviour behind a low score. Rubric's own failure language. */
  problem: string;
  /** What to coach. One move, not a lecture. */
  fix: string;
}

export const DIMENSION_GUIDANCE: Record<RubricDimensionKey, DimensionGuidance> = {
  up_front_contract_posture: {
    problem:
      'Reps open with small talk or go straight to pitching. No agenda, no mutual outcomes, and the prospect is never given permission to say no.',
    fix: 'Coach the up-front contract: purpose, both sides’ outcomes, time check, and an explicit right to say no.'
  },
  opening_question_vs_story: {
    problem:
      'Calls open with company background or a product overview, and the rep does most of the talking while the prospect nods along.',
    fix: 'Open with a question, not a story. Hand agenda control to the prospect and let them work.'
  },
  pain_discovery_levels: {
    problem:
      'Discovery stops at the operational answer. Nobody asks what the problem costs the business, and it never becomes personal to the buyer.',
    fix: 'Work the ladder — surface, then business impact, then personal impact — and stay in it instead of rescuing.'
  },
  pain_with_price_tag: {
    problem:
      'Pain is described in adjectives — "frustrating", "a headache" — with no number anywhere. Where a figure does appear, the rep supplied it.',
    fix: 'Get the prospect to say the number out loud, then name the compounding cost of doing nothing for another year.'
  },
  things_prospect_volunteered: {
    problem:
      'Reps walk past the openings prospects hand them — "we can dive into that later" — and never come back to it.',
    fix: 'Catch the volunteered admission when it lands, widen it from a one-off to a pattern, and return to it before the call ends.'
  },
  negative_reverse_reversing: {
    problem:
      'Under resistance reps sell harder: they explain more, work to convince, and answer every question until the call becomes an interview.',
    fix: 'Pull away instead of leaning in. Test how serious the prospect is rather than trying to win them over.'
  },
  equal_business_stature: {
    problem:
      'Reps get more eager and apologetic as the call goes on, treat the prospect as someone to impress, and close by thanking them for their time.',
    fix: 'Vet the prospect rather than auditioning for them. Hold posture under pressure and drop the thank-you close.'
  },
  budget_decision_process: {
    problem:
      'The money conversation is avoided, or asked once and left at a vague answer. The rep assumes whoever is on the call can sign.',
    fix: 'Qualify budget properly, map who else has to say yes, and name "do nothing" as the outcome it usually is.'
  },
  up_front_contract_close: {
    problem:
      'Calls end on "I’ll send you something" or "let’s stay in touch". Only the rep has an action, and it is tied to their calendar rather than the prospect’s pain.',
    fix: 'Close on a binary next step — yes or no, not maybe — with a commitment from both sides, anchored to the pain they named.'
  },
  coachs_discipline: {
    problem:
      'Reps start selling the moment a problem is named and give strategy away for free, auditioning instead of qualifying.',
    fix: 'Hold the pitch until pain and budget are both qualified. Diagnose, do not prescribe.'
  }
};
